<template>
  <div
    v-if="entries.length"
    class="flex shrink-0 flex-col justify-center"
    :class="{ 'gap-0.5': entries.length > 1 }"
    :data-testid="`conversion-${part}`"
  >
    <div
      v-for="(entry, index) in entries"
      :key="entry.document?.id ?? index"
      class="flex items-center gap-1 leading-none"
      :class="{
        'flex-row-reverse justify-end': part === 'from',
        'flex-row': part !== 'from'
      }"
      data-testid="linked-document"
    >
      <!-- One document: a straight arrow. Several: the arrows fan out from the reference -->
      <FontAwesomeIcon
        :icon="['fal', 'arrow-right']"
        class="text-xs text-slate-400"
        :class="entry.arrowClass"
        aria-hidden="true"
      />
      <component
        :is="entry.document?.id ? 'RouterLink' : 'span'"
        v-bind="entry.linkProps"
        class="flex"
        :class="{
          'text-slate-500 dark:text-slate-400': part === 'from',
          'text-primary': part !== 'from'
        }"
        :role="entry.document?.id ? null : 'img'"
        :aria-label="entry.label"
        :title="entry.label"
        data-testid="linked-document-icon"
        @click.stop
      >
        <FontAwesomeIcon :icon="['fal', entry.icon]" />
      </component>
    </div>
    <span
      v-if="hiddenCount"
      ref="more"
      tabindex="0"
      role="button"
      :aria-label="hiddenLabel"
      class="cursor-default self-center rounded-full bg-slate-200 px-1.5 py-0.5 text-xs leading-none font-semibold text-slate-700 dark:bg-slate-600 dark:text-slate-100"
      data-testid="linked-documents-more"
      @mouseenter="openMore"
      @mouseleave="scheduleClose"
      @focus="openMore"
      @blur="scheduleClose"
      @click.stop="openMore"
      @keydown.enter.prevent.stop="openMore"
      @keydown.esc="closeMore"
    >
      +{{ hiddenCount }}
    </span>
    <!-- Teleported: the title cell clips its overflow -->
    <Teleport to="body">
      <ul
        v-if="moreOpened"
        class="fixed z-100 flex flex-col gap-1 rounded border border-slate-200 bg-white p-2 text-sm shadow-lg dark:border-slate-600 dark:bg-slate-700 dark:text-gray-300"
        :style="moreStyle"
        data-testid="linked-documents-more-list"
        @mouseenter="cancelClose"
        @mouseleave="scheduleClose"
      >
        <li
          v-for="(document, index) in hiddenDocuments"
          :key="document.id ?? index"
        >
          <component
            :is="document.id ? 'RouterLink' : 'span'"
            v-bind="linkPropsOf(document)"
            class="flex items-center gap-2 whitespace-nowrap"
            :class="{ 'hover:underline': document.id }"
            data-testid="linked-documents-more-item"
            @click.stop
          >
            <FontAwesomeIcon
              :icon="['fal', iconOf(document)]"
              :class="{
                'text-slate-500 dark:text-slate-400': part === 'from',
                'text-primary': part !== 'from'
              }"
            />
            {{ documentLabel(document) }}
          </component>
        </li>
      </ul>
    </Teleport>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import DocTypes from '@/assets/enums/invoicing/DocTypes'
import { linkedDocumentsVisibility } from '@c/Dashboard/Content/Invoicing/linkedDocuments'

const ICONS = {
  [DocTypes.QUOTE]: 'file-lines',
  [DocTypes.PURCHASE_ORDER]: 'file-signature',
  [DocTypes.INVOICE]: 'file-invoice-dollar',
  [DocTypes.INVOICE_PROFORMA]: 'file-invoice'
}

// Documents drawn at most: the others are only counted, with a "+n"
const MAX_DOCUMENTS = 2

export default {
  name: 'InvoiceConversionIndicator',
  components: { FontAwesomeIcon },
  props: {
    // `{ from, to, from_documents, to_documents }` sent by the API for the conversion field
    conversion: { type: Object, default: null },
    // Type of the document of the row
    docType: { type: String, default: null },
    // `from`: where the document comes from, `to`: what it was converted into
    part: {
      type: String,
      required: true,
      validator: (value) => ['from', 'to'].includes(value)
    }
  },
  data() {
    return { moreOpened: false, moreStyle: {}, closeTimer: null }
  },
  computed: {
    visible() {
      return linkedDocumentsVisibility(this.conversion, this.docType)[this.part]
    },
    documents() {
      const documents =
        this.part === 'from'
          ? this.conversion?.from_documents
          : this.conversion?.to_documents
      return documents ?? []
    },
    // Documents the cell has no room for
    hiddenDocuments() {
      return this.visible ? this.documents.slice(MAX_DOCUMENTS) : []
    },
    hiddenCount() {
      return this.hiddenDocuments.length
    },
    entries() {
      if (!this.visible) return []

      // The API says the link exists but sends no detail: one icon, without a link
      const documents = this.documents.length ? this.documents : [null]
      const shown = documents.slice(0, MAX_DOCUMENTS)

      return shown.map((document, index) => ({
        document,
        icon: this.iconOf(document),
        label: this.labelOf(document),
        arrowClass: this.arrowClass(index, shown.length),
        linkProps: this.linkPropsOf(document)
      }))
    },
    // Read aloud by screen readers, since the list itself only opens on hover/focus/click
    hiddenLabel() {
      return this.hiddenDocuments
        .map((document) => this.documentLabel(document))
        .join(', ')
    }
  },
  beforeUnmount() {
    this.closeMore()
  },
  methods: {
    openMore() {
      this.cancelClose()
      const rect = this.$refs.more.getBoundingClientRect()
      this.moreStyle = { top: `${rect.bottom}px`, left: `${rect.left}px` }
      this.moreOpened = true
      // The list is fixed: it would stay behind while the table scrolls
      window.addEventListener('scroll', this.closeMore, true)
    },
    closeMore() {
      this.cancelClose()
      this.moreOpened = false
      window.removeEventListener('scroll', this.closeMore, true)
    },
    // Small delay so the pointer can travel from the "+n" to the list
    scheduleClose() {
      this.cancelClose()
      this.closeTimer = setTimeout(this.closeMore, 150)
    },
    cancelClose() {
      clearTimeout(this.closeTimer)
    },
    iconOf(document) {
      return ICONS[document?.doc_type] ?? 'file-invoice'
    },
    documentLabel(document) {
      return [
        this.$t(`enums.invoicing.docTypes.${document.doc_type}`),
        document.reference
      ]
        .filter(Boolean)
        .join(' ')
    },
    linkPropsOf(document) {
      return document?.id
        ? {
            to: {
              name: 'invoice_show_route',
              params: { invoicing_id: document.id }
            }
          }
        : {}
    },
    // Straight for a single document; otherwise the first goes up, the last goes down.
    // Arrows towards the reference (`from`) mirror those leaving it (`to`).
    arrowClass(index, total) {
      if (total === 1) return ''
      const middle = (total - 1) / 2
      if (index === middle) return ''
      const goesUp = index < middle
      const up = this.part === 'to' ? goesUp : !goesUp
      return up ? '-rotate-45' : 'rotate-45'
    },
    labelOf(document) {
      const base =
        this.part === 'from'
          ? this.$t('invoicing.dashboard.comes_from_conversion')
          : this.$t('invoicing.dashboard.converted')

      if (!document?.doc_type) return base

      const docType = this.$t(`enums.invoicing.docTypes.${document.doc_type}`)
      return [
        base,
        [docType, document.reference].filter(Boolean).join(' ')
      ].join(' : ')
    }
  }
}
</script>
